import React, { useState } from 'react';
import { Facebook, Instagram, Linkedin, Youtube, ThumbsUp, MessageCircle, Share2, Heart, Send, Bookmark, MoreHorizontal, Globe, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PLATFORM_COLORS } from '../../../utils/socialMediaHelpers';

const PLATFORM_TABS = [
    { id: 'facebook', icon: Facebook, label: 'Facebook' },
    { id: 'instagram', icon: Instagram, label: 'Instagram' },
    { id: 'linkedin', icon: Linkedin, label: 'LinkedIn' },
    { id: 'youtube', icon: Youtube, label: 'YouTube' },
];

// Facebook Post Preview
const FacebookPreview = ({ content, mediaUrls, accountName, avatarUrl }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold overflow-hidden">
                {avatarUrl ? (
                    <img src={avatarUrl} alt={accountName} className="w-full h-full object-cover" />
                ) : (
                    accountName?.charAt(0)?.toUpperCase() || 'C'
                )}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">{accountName || 'CloudMortgage.io'}</p>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>Just now</span>
                    <span>·</span>
                    <Globe className="w-3 h-3" />
                </div>
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreHorizontal className="w-5 h-5 text-gray-500" />
            </button>
        </div>

        {/* Content */}
        <div className="px-3 pb-3">
            <p className="text-gray-900 text-sm whitespace-pre-wrap">
                {content || <span className="text-gray-400 italic">Your post content will appear here...</span>}
            </p>
        </div>

        {/* Media */}
        {mediaUrls && mediaUrls.length > 0 && (
            <div className="bg-gray-100">
                {mediaUrls[0]?.type?.startsWith('video') ? (
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                        <Play className="w-16 h-16 text-white opacity-80" />
                    </div>
                ) : (
                    <img src={mediaUrls[0]?.url} alt="Post media" className="w-full object-cover max-h-96" />
                )}
            </div>
        )}

        {/* Engagement Stats */}
        <div className="px-3 py-2 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
            <div className="flex items-center gap-1">
                <div className="flex -space-x-1">
                    <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center">
                        <ThumbsUp className="w-2.5 h-2.5 text-white" />
                    </span>
                    <span className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                        <Heart className="w-2.5 h-2.5 text-white" />
                    </span>
                </div>
                <span>24</span>
            </div>
            <div className="flex items-center gap-3">
                <span>3 comments</span>
                <span>2 shares</span>
            </div>
        </div>

        {/* Actions */}
        <div className="px-3 py-1 flex items-center justify-around">
            <button className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-sm font-medium">Like</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Comment</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Share2 className="w-5 h-5" />
                <span className="text-sm font-medium">Share</span>
            </button>
        </div>
    </div>
);

// Instagram Post Preview
const InstagramPreview = ({ content, mediaUrls, accountName, avatarUrl }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-sm">
        {/* Header */}
        <div className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 p-0.5">
                <div className="w-full h-full rounded-full bg-white p-0.5">
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold overflow-hidden">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt={accountName} className="w-full h-full object-cover" />
                        ) : (
                            accountName?.charAt(0)?.toUpperCase() || 'C'
                        )}
                    </div>
                </div>
            </div>
            <p className="font-semibold text-gray-900 text-sm flex-1">{accountName || 'cloudmortgage.io'}</p>
            <button className="p-1">
                <MoreHorizontal className="w-5 h-5 text-gray-900" />
            </button>
        </div>

        {/* Media */}
        <div className="aspect-square bg-gray-100 flex items-center justify-center">
            {mediaUrls && mediaUrls.length > 0 ? (
                mediaUrls[0]?.type?.startsWith('video') ? (
                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                        <Play className="w-16 h-16 text-white opacity-80" />
                    </div>
                ) : (
                    <img src={mediaUrls[0]?.url} alt="Post media" className="w-full h-full object-cover" />
                )
            ) : (
                <div className="text-center p-8">
                    <Instagram className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Add an image or video</p>
                </div>
            )}
        </div>

        {/* Actions */}
        <div className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
                <Heart className="w-6 h-6 text-gray-900" />
                <MessageCircle className="w-6 h-6 text-gray-900" />
                <Send className="w-6 h-6 text-gray-900" />
            </div>
            <Bookmark className="w-6 h-6 text-gray-900" />
        </div>

        {/* Likes */}
        <div className="px-3 pb-1">
            <p className="text-sm font-semibold text-gray-900">128 likes</p>
        </div>

        {/* Caption */}
        <div className="px-3 pb-3">
            <p className="text-sm text-gray-900">
                <span className="font-semibold">{accountName || 'cloudmortgage.io'}</span>{' '}
                {content || <span className="text-gray-400 italic">Your caption will appear here...</span>}
            </p>
        </div>
    </div>
);

// LinkedIn Post Preview
const LinkedInPreview = ({ content, mediaUrls, accountName, avatarUrl }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-3 flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold overflow-hidden">
                {avatarUrl ? (
                    <img src={avatarUrl} alt={accountName} className="w-full h-full object-cover" />
                ) : (
                    accountName?.charAt(0)?.toUpperCase() || 'C'
                )}
            </div>
            <div className="flex-1">
                <p className="font-semibold text-gray-900 text-sm">{accountName || 'CloudMortgage.io'}</p>
                <p className="text-xs text-gray-500">Mortgage Technology Company</p>
                <p className="text-xs text-gray-500">Just now · <Globe className="w-3 h-3 inline" /></p>
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreHorizontal className="w-5 h-5 text-gray-500" />
            </button>
        </div>

        {/* Content */}
        <div className="px-3 pb-3">
            <p className="text-gray-900 text-sm whitespace-pre-wrap">
                {content || <span className="text-gray-400 italic">Your post content will appear here...</span>}
            </p>
        </div>

        {/* Media */}
        {mediaUrls && mediaUrls.length > 0 && (
            <div className="bg-gray-100">
                {mediaUrls[0]?.type?.startsWith('video') ? (
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                        <Play className="w-16 h-16 text-white opacity-80" />
                    </div>
                ) : (
                    <img src={mediaUrls[0]?.url} alt="Post media" className="w-full object-cover max-h-96" />
                )}
            </div>
        )}

        {/* Engagement */}
        <div className="px-3 py-2 flex items-center justify-between text-xs text-gray-500 border-b border-gray-100">
            <div className="flex items-center gap-1">
                <span className="flex -space-x-1">
                    <span className="w-4 h-4 rounded-full bg-blue-500" />
                    <span className="w-4 h-4 rounded-full bg-green-500" />
                    <span className="w-4 h-4 rounded-full bg-red-400" />
                </span>
                <span>42</span>
            </div>
            <span>8 comments · 3 reposts</span>
        </div>

        {/* Actions */}
        <div className="px-2 py-1 flex items-center justify-around">
            <button className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-xs font-medium">Like</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs font-medium">Comment</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Share2 className="w-5 h-5" />
                <span className="text-xs font-medium">Repost</span>
            </button>
            <button className="flex-1 flex items-center justify-center gap-2 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Send className="w-5 h-5" />
                <span className="text-xs font-medium">Send</span>
            </button>
        </div>
    </div>
);

// YouTube Post Preview (Community Post)
const YouTubePreview = ({ content, mediaUrls, accountName, avatarUrl, platformSettings }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-3 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center text-white font-bold overflow-hidden">
                {avatarUrl ? (
                    <img src={avatarUrl} alt={accountName} className="w-full h-full object-cover" />
                ) : (
                    accountName?.charAt(0)?.toUpperCase() || 'C'
                )}
            </div>
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900 text-sm">{accountName || 'CloudMortgage.io'}</p>
                    <span className="text-xs text-gray-500">Just now</span>
                </div>
                {platformSettings?.youtube?.title && (
                    <p className="text-sm font-medium text-gray-900 mt-1">{platformSettings.youtube.title}</p>
                )}
            </div>
            <button className="p-2 hover:bg-gray-100 rounded-full">
                <MoreHorizontal className="w-5 h-5 text-gray-500" />
            </button>
        </div>

        {/* Content */}
        <div className="px-3 pb-3">
            <p className="text-gray-900 text-sm whitespace-pre-wrap">
                {content || <span className="text-gray-400 italic">Your post content will appear here...</span>}
            </p>
        </div>

        {/* Media/Video */}
        {mediaUrls && mediaUrls.length > 0 && (
            <div className="relative">
                {mediaUrls[0]?.type?.startsWith('video') ? (
                    <div className="aspect-video bg-gray-900 flex items-center justify-center">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center">
                                <Play className="w-8 h-8 text-white ml-1" />
                            </div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-1 rounded">
                            0:00
                        </div>
                    </div>
                ) : (
                    <img src={mediaUrls[0]?.url} alt="Post media" className="w-full object-cover max-h-96" />
                )}
            </div>
        )}

        {/* Actions */}
        <div className="px-3 py-3 flex items-center gap-6">
            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ThumbsUp className="w-5 h-5" />
                <span className="text-sm">Like</span>
            </button>
            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ThumbsUp className="w-5 h-5 rotate-180" />
            </button>
            <button className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">Comment</span>
            </button>
        </div>
    </div>
);

const PostPreview = ({
    content,
    mediaUrls = [],
    selectedPlatforms = [],
    accountName = 'CloudMortgage.io',
    avatarUrl = null,
    platformSettings = {}
}) => {
    // Default to first selected platform or facebook
    const [activePreview, setActivePreview] = useState(
        selectedPlatforms[0] || 'facebook'
    );

    // Update active preview when selected platforms change
    React.useEffect(() => {
        if (selectedPlatforms.length > 0 && !selectedPlatforms.includes(activePreview)) {
            setActivePreview(selectedPlatforms[0]);
        }
    }, [selectedPlatforms, activePreview]);

    const renderPreview = () => {
        const previewProps = { content, mediaUrls, accountName, avatarUrl, platformSettings };

        switch (activePreview) {
            case 'facebook':
                return <FacebookPreview {...previewProps} />;
            case 'instagram':
                return <InstagramPreview {...previewProps} />;
            case 'linkedin':
                return <LinkedInPreview {...previewProps} />;
            case 'youtube':
                return <YouTubePreview {...previewProps} />;
            default:
                return <FacebookPreview {...previewProps} />;
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-900 rounded-xl overflow-hidden">
            {/* Platform Tabs */}
            <div className="flex items-center gap-1 p-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                {PLATFORM_TABS.map((platform) => {
                    const colors = PLATFORM_COLORS[platform.id];
                    const isActive = activePreview === platform.id;
                    const isSelected = selectedPlatforms.includes(platform.id);

                    return (
                        <button
                            key={platform.id}
                            onClick={() => setActivePreview(platform.id)}
                            className={`p-2 rounded-lg transition-all ${
                                isActive
                                    ? `${colors?.bg || 'bg-slate-100'} ${colors?.dark?.bg || 'dark:bg-slate-700'}`
                                    : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                            } ${!isSelected ? 'opacity-40' : ''}`}
                            title={platform.label}
                        >
                            <platform.icon className={`w-5 h-5 ${
                                isActive
                                    ? `${colors?.text || 'text-slate-600'} ${colors?.dark?.text || 'dark:text-slate-300'}`
                                    : 'text-slate-500 dark:text-slate-400'
                            }`} />
                        </button>
                    );
                })}
            </div>

            {/* Device Frame Toggle */}
            <div className="flex items-center justify-center gap-2 py-2 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                <span className="text-xs text-slate-500 dark:text-slate-400">Preview</span>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-4">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activePreview}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="max-w-md mx-auto"
                    >
                        {renderPreview()}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default PostPreview;
